import type { StatusTone } from '@vnetwork/ui/data';

/**
 * Domain-status → @ui `StatusPill` tone maps for the customer detail tables.
 * The legacy client colored these via per-module option lists (Contract /
 * Invoice / Service constants); the maps below reproduce that intent on the
 * @ui tone palette (error → `danger`, primary tone kept where legacy used it).
 * `humanize` turns a slug into a Title-Case label for display.
 */

/** Title-case a `dash-separated` status slug: `waiting-confirm` → `Waiting Confirm`. */
export function humanize(value?: string | null): string {
  if (!value) return '—';
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Customer top-level status → tone (header pill). */
export function customerTone(status?: string): StatusTone {
  switch (status) {
    case 'active':
      return 'success';
    case 'trial':
    case 'poc':
    case 'qualified-prospect':
      return 'info';
    case 'dead-lead':
    case 'stop':
      return 'danger';
    case 'coollead':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Contract status → tone. active=success, outdate/retained=danger, pending/exchanging=info/primary. */
export function contractTone(status?: string): StatusTone {
  switch (status) {
    case 'active':
      return 'success';
    case 'outdate':
    case 'retained':
      return 'danger';
    case 'uploading':
      return 'warning';
    case 'pending':
    case 'exchanging':
      return 'info';
    default:
      return 'neutral';
  }
}

/** Invoice status → tone. paid/done=success, approved=info, waiting-confirm=info, cancelled/rejected=danger. */
export function invoiceTone(status?: string): StatusTone {
  switch (status) {
    case 'paid':
    case 'done':
      return 'success';
    case 'approved':
      return 'info';
    case 'waiting-confirm':
      return 'info';
    case 'cancelled':
    case 'rejected':
      return 'danger';
    case 'refunded':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Service status → tone. active=success, trialing=info, cancelled/rejected/pending-terminated=danger, waiting-*=warning. */
export function serviceTone(status?: string): StatusTone {
  switch (status) {
    case 'active':
      return 'success';
    case 'trialing':
      return 'info';
    case 'cancelled':
    case 'rejected':
    case 'pending-terminated':
      return 'danger';
    case 'on-resubmit-information':
      return 'warning';
    default:
      if (status && status.startsWith('waiting')) return 'warning';
      return 'neutral';
  }
}
